import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, LogIn } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import {
  fetchSession,
  navigateTo,
  requestPasswordReset,
  safeNextPath,
  signIn,
  signOut,
} from '@/features/auth/api';
import { ApiError } from '@/lib/api';

/**
 * The console's sign-in page.
 *
 * Rendered outside the session provider, since there is no session yet. A
 * visitor who already has one is sent straight on; a visitor signed in to an
 * account without console access is told so and offered a way out, rather
 * than bouncing between this page and the shell.
 */
export default function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));

  const session = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: ({ signal }) => fetchSession(signal),
    retry: false,
    staleTime: 0,
  });

  React.useEffect(() => {
    document.title = 'Sign in · Admin Console';
  }, []);

  React.useEffect(() => {
    if (session.data?.authenticated && session.data.has_console_access) navigateTo(next);
  }, [session.data, next]);

  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'signin' | 'reset'>('signin');

  const login = useMutation({
    mutationFn: () => signIn({ username: username.trim(), password, next }),
    onSuccess: (result) => navigateTo(result.next || next),
    onError: (failure) => {
      setError(failure instanceof Error ? failure.message : 'Could not sign in.');
      setPassword('');
    },
  });

  const leave = useMutation({
    mutationFn: signOut,
    onSuccess: () => void session.refetch(),
  });

  const businessName = session.data?.business_name || 'Wards Equipment Rental';
  const ready = Boolean(username.trim() && password) && !login.isPending;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setError(null);
    login.mutate();
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-muted px-4 py-10 text-ink">
      <div className="w-full max-w-[22rem]">
        <div className="mb-5 text-center">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink-4">{businessName}</div>
          <h1 className="mt-1 text-[22px] font-semibold tracking-[-0.015em]">Admin console</h1>
        </div>

        <div className="rounded-xl border border-line bg-surface p-5 shadow-pop">
          {session.isPending ? (
            <div className="flex justify-center py-6" role="status" aria-label="Checking your session">
              <Spinner />
            </div>
          ) : session.data?.authenticated && session.data.has_console_access ? (
            <p role="status" className="py-4 text-center text-[13px] text-ink-2">
              You are already signed in. Taking you to the console…
            </p>
          ) : session.data?.authenticated && !session.data.has_console_access ? (
            <NoAccess
              name={session.data.user?.display_name || session.data.user?.username || 'this account'}
              busy={leave.isPending}
              onSignOut={() => leave.mutate()}
            />
          ) : mode === 'reset' ? (
            <ResetForm initialEmail={username.includes('@') ? username : ''} onBack={() => setMode('signin')} />
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3.5" noValidate>
              <Field label="Username" htmlFor="login-username">
                <Input
                  id="login-username"
                  value={username}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoFocus
                  onChange={(event) => setUsername(event.target.value)}
                />
              </Field>
              <Field label="Password" htmlFor="login-password">
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              {error ? (
                <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-[12px] text-danger">
                  {error}
                </p>
              ) : null}

              <Button type="submit" variant="primary" size="lg" disabled={!ready} loading={login.isPending} className="mt-1">
                <LogIn />
                Sign in
              </Button>

              <button
                type="button"
                className="self-center text-[12px] text-ink-3 underline-offset-2 hover:text-ink hover:underline"
                onClick={() => {
                  setError(null);
                  setMode('reset');
                }}
              >
                Forgot your password?
              </button>
            </form>
          )}
        </div>

        {session.data?.legacy_login_url ? (
          <p className="mt-4 text-center text-[11px] text-ink-4">
            Need the break-glass admin?{' '}
            <a href={session.data.legacy_login_url} className="underline-offset-2 hover:text-ink hover:underline">
              Legacy Django sign-in
            </a>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function NoAccess({ name, busy, onSignOut }: { name: string; busy: boolean; onSignOut: () => void }) {
  return (
    <div className="flex flex-col gap-3 text-center">
      <p className="text-[13px] text-ink-2">
        You are signed in as <span className="font-semibold text-ink">{name}</span>, but that account does not
        have access to the admin console.
      </p>
      <p className="text-[12px] text-ink-3">Ask an administrator to grant it, or sign in with another account.</p>
      <Button variant="secondary" onClick={onSignOut} loading={busy}>
        Sign out and switch account
      </Button>
    </div>
  );
}

function ResetForm({ initialEmail, onBack }: { initialEmail: string; onBack: () => void }) {
  const [email, setEmail] = React.useState(initialEmail);
  const [sent, setSent] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const request = useMutation({
    mutationFn: () => requestPasswordReset(email.trim()),
    onSuccess: (result) => setSent(result.message || 'If an account with this email exists, a reset link is on its way.'),
    onError: (failure) =>
      setError(failure instanceof ApiError ? failure.message : 'The reset link could not be sent. Try again in a moment.'),
  });

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim() || request.isPending) return;
    setError(null);
    request.mutate();
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3.5" noValidate>
      <div>
        <h2 className="flex items-center gap-1.5 text-[14px] font-semibold">
          <KeyRound className="size-4 text-ink-3" />
          Reset your password
        </h2>
        <p className="mt-1 text-[12px] text-ink-3">
          Enter the email on your account. The link lets you choose a new password and expires in 30 minutes.
        </p>
      </div>

      {sent ? (
        <p role="status" className="rounded-md bg-positive-soft px-3 py-2 text-[12px] text-positive">
          {sent}
        </p>
      ) : (
        <Field label="Email" htmlFor="reset-email">
          <Input
            id="reset-email"
            type="email"
            value={email}
            autoComplete="email"
            autoFocus
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
      )}

      {error ? (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-[12px] text-danger">
          {error}
        </p>
      ) : null}

      <div className="mt-1 flex items-center justify-between gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          Back to sign in
        </Button>
        {sent ? null : (
          <Button type="submit" variant="primary" size="sm" disabled={!email.trim()} loading={request.isPending}>
            Send reset link
          </Button>
        )}
      </div>
    </form>
  );
}
